<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import * as yup from 'yup';
import { usersApi } from '@/api/users';
import type { AxiosError } from 'axios';

const { t } = useI18n();

const MIN_PASSWORD_LENGTH = 8;

const validationSchema = yup.object({
  email: yup.string().email(t('login.emailInvalid')).required(t('login.emailRequired')),
  password: yup.string().min(MIN_PASSWORD_LENGTH, t('login.passwordMinLength')).required(t('login.passwordRequired')),
});

const { handleSubmit, values, errors, defineField } = useForm({ validationSchema });

const [email, emailAttrs] = defineField('email');
const [password, passwordAttrs] = defineField('password');

const { mutate: createUser, isError, error } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => usersApi.login({ ...values }),
    onSuccess: () => { window.location.href = '/caregivers'; },
  },
);

const onSubmit = handleSubmit(() => createUser());

const errorText = computed(() => {
  if (isError.value) {
    return (error.value?.response?.data as { [key: string]: string }).error;
  }

  return '';
});

const successfulPasswordUpdate = new URLSearchParams(window.location.search).get('successful_password_update');
const successfulUnlock = new URLSearchParams(window.location.search).get('unlock');
</script>

<template>
  <div>
    <h3 class="mb-2 text-2xl font-semibold">
      {{ t('login.title') }}
    </h3>
    <p class="mb-8 text-gray-400">
      {{ t('login.subtitle') }}
    </p>

    <v-form @submit.prevent="onSubmit">
      <v-text-field
        v-model="email"
        v-bind="emailAttrs"
        variant="outlined"
        :label="t('login.email')"
        :error-messages="errors.email"
      />
      <v-text-field
        v-model="password"
        v-bind="passwordAttrs"
        type="password"
        variant="outlined"
        :label="t('login.password')"
        :error-messages="errors.password"
      />
      <div class="mt-4 align-center d-flex justify-space-between">
        <v-btn
          color="primary"
          type="submit"
        >
          {{ t('login.submit') }}
        </v-btn>
        <v-btn
          variant="text"
          color="primary"
          size="x-small"
          :href="'/users/password/new'"
        >
          {{ t('login.forgotPassword') }}
        </v-btn>
      </div>
    </v-form>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ errorText || t('userSession.defaultError') }}
    </v-alert>
    <v-alert
      v-if="successfulPasswordUpdate === 'true'"
      class="mt-4 rounded"
      :type="'success'"
    >
      {{ t('login.passwordUpdated') }}
    </v-alert>
    <v-alert
      v-if="successfulUnlock === 'true'"
      class="mt-4 rounded"
      :type="'success'"
    >
      {{ t('login.accountUnlocked') }}
    </v-alert>
  </div>
</template>
